import { TokenChainAvatar } from '@components/ChainLogo'
import { Chain } from '@plug-core/interfaces/common'
import {
  GroupedBalance,
  TokenBalance,
  tokenBalanceToBalanceGroup,
} from '@plug-core/providers/BalanceProvider'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { ListGroup, ListRow, cn } from '@ui'
import { AnimatePresence, motion } from 'framer-motion'
import React, { createContext, useContext, useState } from 'react'

const MAX_VISIBLE_CHAINS = 2

/** Set by `TokenList`; a lone `TokenListItem` wraps itself in its own card. */
export const InTokenListContext = createContext(false)

export interface TokenListItemProps {
  balance: GroupedBalance | TokenBalance
  onClick?: (token: TokenBalance) => void
  showPriceChange: boolean
}

const TokenBalanceItem = ({
  onClick,
  chains,
  logo,
  name,
  balance,
  usdBalance,
  symbol,
  priceChange,
  dense,
  expanded,
  className,
}: {
  onClick?: () => void
  chains: Chain[]
  logo: string
  name: string
  symbol: string
  balance: number
  usdBalance: number | undefined
  priceChange?: number | null
  dense?: boolean
  /** Set for groups: exposes the expanded state to assistive tech. */
  expanded?: boolean
  className?: string
}) => (
  <ListRow
    className={className}
    role={onClick ? undefined : 'none'}
    aria-expanded={expanded}
    dense={dense}
    onClick={onClick}
    leading={
      <TokenChainAvatar
        logo={logo}
        chains={chains}
        size={dense ? 32 : 40}
        maxChains={MAX_VISIBLE_CHAINS}
      />
    }
    primary={name}
    secondary={<span className="tabular">{formatNumber(balance, symbol)}</span>}
    trailing={
      <>
        <span className="flex flex-col items-end gap-0.5">
          <span className="tabular text-[15px] leading-[1.3] font-semibold text-fg">
            {formatMoney(usdBalance)}
          </span>
          {typeof priceChange === 'number' && (
            <span
              className={cn(
                'tabular text-[13px] leading-[1.4]',
                (priceChange || 0) > 0 ? 'text-success' : 'text-danger'
              )}
            >
              {`${priceChange?.toFixed(2)}%`}
            </span>
          )}
        </span>
      </>
    }
  />
)

export const TokenListItem = ({ balance, onClick, showPriceChange }: TokenListItemProps) => {
  const [expanded, setExpanded] = useState(false)
  const inList = useContext(InTokenListContext)

  const groupBalance = 'balances' in balance ? balance : tokenBalanceToBalanceGroup(balance)
  const { logo, balances, usdBalance, totalHumanBalance, priceChange, name, symbol } = groupBalance
  const isGroup = balances.length > 1

  const content = (
    <div role="listitem">
      <TokenBalanceItem
        onClick={
          isGroup || onClick
            ? () => (isGroup ? setExpanded(!expanded) : onClick?.(balances[0]))
            : undefined
        }
        chains={balances.map((b) => b.token.chain)}
        logo={logo}
        name={name}
        symbol={symbol}
        balance={totalHumanBalance}
        usdBalance={usdBalance}
        priceChange={showPriceChange ? priceChange : null}
        expanded={isGroup ? expanded : undefined}
      />

      <AnimatePresence initial={false}>
        {isGroup && expanded && (
          <motion.section
            className="overflow-hidden"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 },
            }}
          >
            <div className="flex flex-col border-t border-solid border-hairline py-1">
              {groupBalance.balances.map((tokenBalance) => (
                <TokenBalanceItem
                  key={tokenBalance.token.id}
                  dense
                  className="px-4"
                  onClick={() => onClick?.(tokenBalance)}
                  chains={[tokenBalance.token.chain]}
                  logo={tokenBalance.token.logo}
                  name={tokenBalance.token.name}
                  symbol={tokenBalance.token.symbol}
                  balance={tokenBalance.humanBalance}
                  usdBalance={tokenBalance.usdBalance}
                />
              ))}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  )

  return inList ? content : <ListGroup>{content}</ListGroup>
}
